import { nextTick } from 'vue'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import i18n from '@lingoking-node-apps/i18n'
import { TokenService } from '@lingoking-node-apps/api'
import { useAuthStore } from '@/stores/auth'
import { saveLocale } from '@lingoking-node-apps/i18n'
import { extractLocaleFromPath } from '@/utils/locale'

const routes: RouteRecordRaw[] = [
  {
    path: '',
    name: 'home',
    meta: {
      title: 'home'
    },
    component: () => import('@/views/HomeView.vue')
  },
  {
    path: '/login/:locale?',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: {
      title: 'login',
      public: true,
      agnostic: true
    }
  },
  {
    path: '/register/:locale?',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: {
      title: 'register',
      public: true
    }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: {
      title: 'forgot_password',
      public: true
    }
  },
  {
    path: '/password-reset/:token',
    name: 'password-reset',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: {
      title: 'password_reset',
      public: true
    }
  },
  {
    path: '/email-confirmation/:token',
    name: 'email-confirmation',
    component: () => import('@/views/EmailConfirmationView.vue'),
    meta: {
      title: 'email_confirmation',
      public: true
    }
  },
  {
    path: '/select-customer',
    name: 'select-customer',
    component: () => import('@/views/SelectCustomerView.vue'),
    meta: {
      title: 'select_customer',
      agnostic: true
    }
  },
  {
    path: '/switch-customer',
    name: 'switch-customer',
    component: () => import('@/views/SwitchCustomerView.vue'),
    meta: {
      title: 'switch_customer',
      agnostic: true
    }
  },
  {
    path: '/configurator/:id',
    name: 'configurator',
    component: () => import('@/views/ConfiguratorView.vue'),
    meta: {
      title: 'configurator',
      public: true
    }
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import('@/views/CheckoutView.vue'),
    meta: {
      title: 'checkout',
      public: true
    }
  },
  {
    path: '/order-list',
    name: 'order-list',
    component: () => import('@/views/OrderListView.vue'),
    meta: {
      title: 'order_list'
    }
  },
  {
    path: '/my-orders',
    name: 'my-orders',
    component: () => import('@/views/MyOrdersView.vue'),
    meta: {
      title: 'my_orders'
    }
  },
  {
    path: '/my-invoices',
    name: 'my-invoices',
    component: () => import('@/views/MyInvoicesView.vue'),
    meta: {
      title: 'my_invoices'
    }
  },
  {
    path: '/orders-source-upload',
    name: 'orders-source-upload',
    component: () => import('@/views/OrdersSourceUploadView.vue'),
    meta: {
      title: 'order_source_upload'
    }
  },
  {
    path: '/order-detail/:id',
    name: 'order-detail',
    component: () => import('@/views/ItemView.vue'),
    meta: {
      title: 'order_detail'
    }
  },
  {
    path: '/new-translation',
    name: 'new-translation',
    component: () => import('@/views/NewTranslationView.vue'),
    meta: {
      title: 'new_translation'
    }
  },
  {
    path: '/my-profile-settings',
    name: 'my-profile-settings',
    component: () => import('@/views/MyProfileSettingsView.vue'),
    meta: {
      title: 'my_profile_settings'
    }
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import('@/views/LogoutView.vue'),
    meta: {
      title: 'logout',
      agnostic: true
    }
  },
  {
    path: '/oauth/callback/login',
    name: 'oauth_login_callback',
    component: () => import('@/views/OAuthCallbackView.vue'),
    meta: {
      title: 'oauth_login_callback',
      public: true
    }
  },
  {
    path: '/oauth/register/finalize',
    name: 'oauth_register_finalize',
    component: () => import('@/views/SocialRegisterFinalizeView.vue'),
    meta: {
      title: 'oauth_register_finalize',
      public: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'PageNotFound',
    component: () => import('@/views/PageNotFoundView.vue'),
    meta: {
      title: 'not_found'
    }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

router.beforeEach(async (to) => {
  if (to.meta.title) {
    document.title = `Lingoking - customer area - ${i18n.global.t(to.meta.title)}`
  }

  const auth = useAuthStore()

  // JTW:
  const isAuthenticated = TokenService.getAccessToken()

  // load profile:
  if (isAuthenticated && !auth.user) {
    await auth.profile()
  }


  return validateRoute(to, isAuthenticated)
})

router.afterEach((to, from, failure) => {
  if (!failure) {
    nextTick(() => {
      window.posthog?.capture('$pageview', { path: to.fullPath })
      window.Intercom('update')
    })
  }
})

function validateRoute(to: any, isAuthenticated: any) {
  const auth = useAuthStore()

  const authRequired = !to.meta.public
  const isNotAgnosticPage = !to.meta.agnostic

  const isAdmin = auth.isAdmin || auth.isPm || auth.admin
  const selectUserRequired = isAdmin && !auth.hasSwitchUser && isNotAgnosticPage
  const profileFinalizeRequired = authRequired && to.name !== 'oauth_register_finalize' && auth.requireProfileCompleted

  // auth required routes:
  if (authRequired && !isAuthenticated) {
    auth.returnUrl = to.fullPath

    const locale = extractLocaleFromPath(to.path)
    saveLocale(locale)

    return '/login'
  }

  // user selection required for PMs:
  if (selectUserRequired) {
    return '/select-customer'
  }

  if(profileFinalizeRequired) {
    auth.returnUrl = to.fullPath

    return '/oauth/register/finalize'
  }

  // already logged in:
  if (to.name === 'login' && isAuthenticated) {
    return '/'
  }
}

export default router
